import { useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { useCallback } from 'react';

import {
  trackGTMEvent,
  useTrackFailedToUploadAsset,
  useTrackUploadAborted,
  useTrackUploadCompleted,
  useTrackUploadError,
  useTrackUploadedAsset,
  useTrackUploadStarted,
} from '@air/analytics';
import { Clips, type GetBoardStatsResponse } from '@air/api';
import { ClipSource, ClipType } from '@air/api/types';
import { isLarge, uploadByIdSelector } from '@air/redux-uploader';
import { type UseUploadManagerProps, useUploadManager } from '@air/upload-manager';
import { isAbortError } from '@air/upload-utils';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type PrivateUploadTaskParams, type UploadTaskParams } from '~/components/Upload/hooks/types';
import { useNavigateToNewVersionOnUpload } from '~/components/Upload/hooks/useNavigateToNewVersionOnUpload';
import { useUploadConcurrencyChangeAnalytics } from '~/components/Upload/hooks/useUploadConcurrencyChangeAnalytics';
import { useFetchClipForPrivateUpload } from '~/components/Upload/privateUpload/useFetchClipForPrivateUpload';
import { useGetLargePrivateUploadTasks } from '~/components/Upload/privateUpload/useGetLargePrivateUploadTasks';
import { useGetSmallPrivateUploadTask } from '~/components/Upload/privateUpload/useGetSmallPrivateUploadTask';
import {
  getPrivateUploadAnalyticsParams,
  getUploadAbortedAnalyticsParams,
  getUploadCompletedAnalyticsParams,
  getUploadErrorAnalyticsParams,
  getUploadStartedAnalyticsParams,
} from '~/components/Upload/utils/uploadAnalytics';
import { isPrivateBoardStatsKey } from '~/components/v2/hooks/queries/usePrivateBoardsStats';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { centralizedClipSelector } from '~/store/centralizedClip/selectors';
import {
  metadataForPrivateUploadSelector,
  privateUploadBoardIdSelector,
} from '~/store/privateUploadMetadata/selectors';
import { useStorage } from '~/swr-hooks/subscriptions/useStorage';
import { defaultClipGetOptions, getClipWithoutWorkspaceIdKey } from '~/swr-hooks/useGetClipWithNoWorkspaceId';
import { getAssetGalleryItemType } from '~/utils/AssetUtils';
import { useAddBoardsToAllLists } from '~/utils/mutateUtils/AllBoards';
import { useAddAssetsToAllViews, useUpdateVersionByAssetId } from '~/utils/mutateUtils/GalleryAssets';
import { getBoardIdFromPath, getClipIdFromPath } from '~/utils/PathUtils';
import { useAirStore } from '~/utils/ReduxUtils';

export const usePrivateUploadManager = () => {
  const { adaptiveConcurrencyEnabled } = useTemporaryFeatureFlags();
  const store = useAirStore();
  const { data: storage } = useStorage();
  const { addBoardsToAllLists } = useAddBoardsToAllLists();
  const { addAssetsToAllViews } = useAddAssetsToAllViews();
  const { updateVersionByAssetId } = useUpdateVersionByAssetId();
  const client = useQueryClient();
  const { fetchClipForPrivateUpload } = useFetchClipForPrivateUpload();
  const { trackUploadedAsset } = useTrackUploadedAsset();
  const { trackFailedToUploadAsset } = useTrackFailedToUploadAsset();
  const { trackUploadAborted } = useTrackUploadAborted();
  const { trackUploadCompleted } = useTrackUploadCompleted();
  const { trackUploadError } = useTrackUploadError();
  const { trackUploadStarted } = useTrackUploadStarted();

  const { getSmallUploadTask } = useGetSmallPrivateUploadTask();
  const { getLargeUploadTasks } = useGetLargePrivateUploadTasks();
  const { onConcurrencyChange } = useUploadConcurrencyChangeAnalytics({ source: 'private_upload' });

  const { navigateToNewVersionOnUpload } = useNavigateToNewVersionOnUpload();

  const getUploadTasks = useCallback<UseUploadManagerProps['getUploadTasks']>(
    ({ upload }) => {
      const onError: Required<UploadTaskParams>['onError'] = ({ error, failedStage, upload, uploadUrlType }) => {
        const knownError = reportErrorToBugsnag({ error, context: 'Error uploading file' });

        if (!isAbortError(knownError)) {
          trackFailedToUploadAsset({
            id: upload.id,
            type: upload.apiUploadData.type,
            source: ClipSource.web,
            error: knownError.message,
          });
        }

        try {
          const analyticsParams = getPrivateUploadAnalyticsParams({
            context: 'private_upload_manager',
            state: store.getState(),
            upload,
          });

          if (isAbortError(knownError)) {
            trackUploadAborted(getUploadAbortedAnalyticsParams({ commonParams: analyticsParams, failedStage, upload }));
          } else {
            trackUploadError(
              getUploadErrorAnalyticsParams({
                commonParams: analyticsParams,
                error: knownError,
                failedStage,
                upload,
                uploadUrlType,
              }),
            );
          }
        } catch (analyticsError) {
          reportErrorToBugsnag({ error: analyticsError, context: 'Failed to track upload error analytics' });
        }
      };

      const onUploadFinished: Required<UploadTaskParams>['onUploadFinished'] = async ({ uploadId, uploadUrlType }) => {
        const upload = uploadByIdSelector(store.getState(), uploadId);

        if (!upload) {
          return;
        }

        try {
          /**
           * This shouldn't be checked on the frontend. It should be checked on the backend.
           */
          if (storage?.usedStorage === 0) {
            trackGTMEvent({ event: 'first_upload' });
          }

          trackUploadedAsset({ clipId: upload.clipId });
          const analyticsParams = getPrivateUploadAnalyticsParams({
            context: 'private_upload_manager',
            state: store.getState(),
            upload,
          });
          trackUploadCompleted(getUploadCompletedAnalyticsParams(analyticsParams, upload, { uploadUrlType }));
        } catch (analyticsError) {
          reportErrorToBugsnag({ error: analyticsError, context: 'Failed to track upload completed analytics' });
        }

        const uploadParentBoardId = privateUploadBoardIdSelector(store.getState(), upload.id);
        const currentBoardId = getBoardIdFromPath(window.location.pathname);
        const boardId = currentBoardId && uploadParentBoardId ? uploadParentBoardId : undefined;

        if (upload.clipId) {
          const uploadMetadata = metadataForPrivateUploadSelector(store.getState(), upload.id);
          const clipGetOptions = { ...defaultClipGetOptions, boardId };
          const clipCacheKey = getClipWithoutWorkspaceIdKey({ clipId: upload.clipId, options: clipGetOptions });
          const clip = await client.ensureQueryData({
            queryKey: clipCacheKey,
            queryFn: () => Clips.getWithNoWorkspaceId({ id: upload.clipId, options: clipGetOptions }),
          });

          if (clip) {
            void updateVersionByAssetId(clip, boardId);

            const shouldIncrementCount = !uploadMetadata?.isVersion;

            client.setQueriesData<GetBoardStatsResponse>(
              {
                predicate: ({ queryKey }) => isPrivateBoardStatsKey(queryKey) && queryKey[1].boardId === boardId,
              },
              (existingData) =>
                existingData
                  ? produce(existingData, (draft) => {
                      if (shouldIncrementCount) {
                        draft.assetCount += clip.type === ClipType.nonMedia ? 0 : 1;
                        draft.fileCount += clip.type === ClipType.nonMedia ? 1 : 0;
                      }
                      draft.sizeBytes += clip.size;
                    })
                  : {
                      assetCount: shouldIncrementCount && clip.type !== ClipType.nonMedia ? 1 : 0,
                      fileCount: shouldIncrementCount && clip.type === ClipType.nonMedia ? 1 : 0,
                      sizeBytes: clip.size,
                    },
            );

            if (!uploadMetadata?.isVersion) {
              await addAssetsToAllViews({
                parentBoardId: uploadParentBoardId ?? boardId,
                assets: [clip],
                type: getAssetGalleryItemType(clip),
              });
            }
          }
        }
      };

      const params: PrivateUploadTaskParams = {
        onBoardCreated: (board) => addBoardsToAllLists([board], board.parentId),
        onUploadFinished,
        uploadId: upload.id,
        onUploadStarted: async ({ maxRetryCount, retryCount, uploadId, uploadUrlType }) => {
          const upload = uploadByIdSelector(store.getState(), uploadId);

          if (upload) {
            try {
              const analyticsParams = getPrivateUploadAnalyticsParams({
                context: 'private_upload_manager',
                state: store.getState(),
                upload,
              });

              trackUploadStarted(
                getUploadStartedAnalyticsParams(analyticsParams, upload, { maxRetryCount, retryCount, uploadUrlType }),
              );
            } catch (analyticsError) {
              reportErrorToBugsnag({ error: analyticsError, context: 'Failed to track upload started analytics' });
            }
          }

          const clip = await fetchClipForPrivateUpload(uploadId);

          if (clip) {
            const centralizedClip = centralizedClipSelector(store.getState());
            // The centralized clip outlives the asset view (e.g. a canvas opened from it), so only follow the
            // new version while that asset is still on screen; otherwise the push lands on a route that doesn't exist.
            const isViewingAsset = getClipIdFromPath(window.location.pathname) === centralizedClip.id;

            if (clip.assetId === centralizedClip.assetId && isViewingAsset) {
              void navigateToNewVersionOnUpload(clip);
            }
          }
        },
        onError,
      };

      if (isLarge(upload)) {
        return getLargeUploadTasks(params);
      } else {
        return [getSmallUploadTask(params)];
      }
    },
    [
      addAssetsToAllViews,
      addBoardsToAllLists,
      client,
      fetchClipForPrivateUpload,
      getLargeUploadTasks,
      getSmallUploadTask,
      navigateToNewVersionOnUpload,
      storage?.usedStorage,
      store,
      trackFailedToUploadAsset,
      trackUploadAborted,
      trackUploadCompleted,
      trackUploadError,
      trackUploadStarted,
      trackUploadedAsset,
      updateVersionByAssetId,
    ],
  );

  return useUploadManager({
    adaptiveConcurrencyEnabled,
    getUploadTasks,
    onConcurrencyChange,
  });
};
